"use client"

import { useState, useEffect, useCallback, useRef } from 'react'

const DIFFICULTIES = {
  easy: { name: 'Easy', rows: 9, cols: 9, mines: 10 },
  medium: { name: 'Medium', rows: 16, cols: 16, mines: 40 },
  expert: { name: 'Expert', rows: 16, cols: 24, mines: 66 },
}

const getNeighborCoords = (r, c, rows, cols) => {
  const neighbors = []
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      if (dr === 0 && dc === 0) continue
      const nr = r + dr
      const nc = c + dc
      if (nr >= 0 && nr < rows && nc >= 0 && nc < cols) {
        neighbors.push([nr, nc])
      }
    }
  }
  return neighbors
}

const createInitialBoard = (rows, cols) => {
  const board = []
  for (let r = 0; r < rows; r++) {
    const row = []
    for (let c = 0; c < cols; c++) {
      row.push({
        row: r,
        col: c,
        isMine: false,
        isRevealed: false,
        isFlagged: false,
        neighborMines: 0
      })
    }
    board.push(row)
  }
  return board
}

export default function MinesweeperApp() {
  const [difficulty, setDifficulty] = useState('medium')
  const config = DIFFICULTIES[difficulty]

  const [board, setBoard] = useState(() => createInitialBoard(config.rows, config.cols))
  const [gameStatus, setGameStatus] = useState('idle') // 'idle' | 'playing' | 'won' | 'lost'
  const [flagMode, setFlagMode] = useState(false)
  const [flagsPlaced, setFlagsPlaced] = useState(0)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [faceEmotion, setFaceEmotion] = useState('normal') // 'normal' | 'scared' | 'won' | 'lost'
  const timerRef = useRef(null)

  // Timer logic
  useEffect(() => {
    if (gameStatus === 'playing') {
      timerRef.current = setInterval(() => {
        setElapsedSeconds((sec) => Math.min(999, sec + 1))
      }, 1000)
    } else {
      if (timerRef.current) clearInterval(timerRef.current)
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [gameStatus])

  const handleSelectDifficulty = (newDiff) => {
    if (newDiff === difficulty && gameStatus === 'idle') return
    const nextConfig = DIFFICULTIES[newDiff]
    setDifficulty(newDiff)
    setBoard(createInitialBoard(nextConfig.rows, nextConfig.cols))
    setGameStatus('idle')
    setFlagsPlaced(0)
    setElapsedSeconds(0)
    setFaceEmotion('normal')
  }

  // Populate mines ensuring the first clicked cell is never a mine
  const populateMines = (initialBoard, firstR, firstC, currentConfig) => {
    const { rows, cols, mines } = currentConfig
    const newBoard = initialBoard.map((row) => row.map((cell) => ({ ...cell })))
    let minesPlaced = 0
    const forbidden = new Set([`${firstR},${firstC}`])
    getNeighborCoords(firstR, firstC, rows, cols).forEach(([nr, nc]) => forbidden.add(`${nr},${nc}`))

    while (minesPlaced < mines) {
      const r = Math.floor(Math.random() * rows)
      const c = Math.floor(Math.random() * cols)
      const key = `${r},${c}`
      if (!forbidden.has(key) && !newBoard[r][c].isMine) {
        newBoard[r][c].isMine = true
        minesPlaced++
      }
    }

    // Calculate neighbor counts
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (!newBoard[r][c].isMine) {
          const neighbors = getNeighborCoords(r, c, rows, cols)
          const count = neighbors.reduce((acc, [nr, nc]) => acc + (newBoard[nr][nc].isMine ? 1 : 0), 0)
          newBoard[r][c].neighborMines = count
        }
      }
    }

    return newBoard
  }

  // Flood fill reveal for 0-neighbor cells
  const revealCellAndNeighbors = (currentBoard, startR, startC, rows, cols) => {
    const nextBoard = currentBoard.map((row) => row.map((c) => ({ ...c })))
    const queue = [[startR, startC]]
    const visited = new Set([`${startR},${startC}`])

    while (queue.length > 0) {
      const [r, c] = queue.shift()
      const cell = nextBoard[r][c]
      if (cell.isFlagged) continue

      cell.isRevealed = true

      if (cell.neighborMines === 0 && !cell.isMine) {
        const neighbors = getNeighborCoords(r, c, rows, cols)
        for (const [nr, nc] of neighbors) {
          const key = `${nr},${nc}`
          if (!visited.has(key) && !nextBoard[nr][nc].isRevealed && !nextBoard[nr][nc].isFlagged) {
            visited.add(key)
            queue.push([nr, nc])
          }
        }
      }
    }
    return nextBoard
  }

  const checkWinCondition = (currentBoard, rows, cols) => {
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cell = currentBoard[r][c]
        if (!cell.isMine && !cell.isRevealed) {
          return false
        }
      }
    }
    return true
  }

  const handleCellClick = (r, c) => {
    if (gameStatus === 'won' || gameStatus === 'lost') return

    if (flagMode) {
      handleToggleFlag(r, c)
      return
    }

    const cell = board[r][c]
    if (cell.isRevealed || cell.isFlagged) return

    let activeBoard = board

    if (gameStatus === 'idle') {
      activeBoard = populateMines(board, r, c, config)
      setBoard(activeBoard)
      setGameStatus('playing')
    }

    const clickedCell = activeBoard[r][c]

    if (clickedCell.isMine) {
      // Reveal all mines on loss
      const finalBoard = activeBoard.map((row) =>
        row.map((item) => (item.isMine ? { ...item, isRevealed: true } : item))
      )
      setBoard(finalBoard)
      setGameStatus('lost')
      setFaceEmotion('lost')
      return
    }

    let nextBoard = revealCellAndNeighbors(activeBoard, r, c, config.rows, config.cols)

    if (checkWinCondition(nextBoard, config.rows, config.cols)) {
      // Auto-flag remaining unflagged mines on victory
      nextBoard = nextBoard.map((row) =>
        row.map((item) => (item.isMine ? { ...item, isFlagged: true } : item))
      )
      setBoard(nextBoard)
      setFlagsPlaced(config.mines)
      setGameStatus('won')
      setFaceEmotion('won')
    } else {
      setBoard(nextBoard)
      setFaceEmotion('normal')
    }
  }

  const handleToggleFlag = (r, c, event = null) => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (gameStatus === 'won' || gameStatus === 'lost') return

    const cell = board[r][c]
    if (cell.isRevealed) return

    if (!cell.isFlagged && flagsPlaced >= config.mines) {
      return
    }

    const nextFlagged = !cell.isFlagged
    setFlagsPlaced((prev) => (nextFlagged ? prev + 1 : prev - 1))

    setBoard((prev) =>
      prev.map((row, rowIdx) =>
        row.map((item, colIdx) =>
          rowIdx === r && colIdx === c ? { ...item, isFlagged: nextFlagged } : item
        )
      )
    )
  }

  const handleResetGame = () => {
    setBoard(createInitialBoard(config.rows, config.cols))
    setGameStatus('idle')
    setFlagsPlaced(0)
    setElapsedSeconds(0)
    setFaceEmotion('normal')
  }

  const getNumberColor = (num) => {
    switch (num) {
      case 1: return 'text-blue-400 font-bold'
      case 2: return 'text-emerald-400 font-bold'
      case 3: return 'text-rose-400 font-bold'
      case 4: return 'text-purple-400 font-bold'
      case 5: return 'text-amber-400 font-bold'
      case 6: return 'text-cyan-400 font-bold'
      case 7: return 'text-pink-400 font-bold'
      case 8: return 'text-white font-bold'
      default: return 'text-gray-400'
    }
  }

  const getFaceIcon = () => {
    if (faceEmotion === 'won') return 'fas fa-smile-beam text-pink-400'
    if (faceEmotion === 'lost') return 'fas fa-dizzy text-rose-400'
    if (faceEmotion === 'scared') return 'fas fa-surprise text-amber-300'
    return 'fas fa-smile text-yellow-400'
  }

  const minesRemaining = Math.max(0, config.mines - flagsPlaced)

  return (
    <div className="flex flex-col items-center justify-center h-full p-2 sm:p-4 select-none overflow-y-auto bg-gray-950 text-white">
      <div className="bg-gray-900/90 border border-white/15 rounded-2xl p-3 sm:p-5 shadow-2xl backdrop-blur-md w-full max-w-[760px] mx-auto flex flex-col my-auto">
        {/* Difficulty Selection */}
        <div className="flex items-center justify-between gap-2 mb-2.5 sm:mb-3 bg-black/40 p-1.5 rounded-xl border border-white/10">
          <span className="text-xs text-gray-400 font-medium pl-2 hidden sm:inline">Difficulty:</span>
          <div className="flex items-center gap-1.5 w-full sm:w-auto justify-center">
            {Object.entries(DIFFICULTIES).map(([key, diff]) => (
              <button
                key={key}
                onClick={() => handleSelectDifficulty(key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  difficulty === key
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-white/5 hover:bg-white/15 text-gray-300'
                }`}
              >
                {diff.name} <span className="opacity-70 text-[10px]">({diff.rows}×{diff.cols})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Header Display */}
        <div className="flex items-center justify-between bg-black/50 border border-white/10 rounded-xl px-3.5 py-2 mb-2.5 sm:mb-3">
          {/* Mine Counter */}
          <div className="flex items-center gap-1.5 font-mono text-lg text-rose-400 font-bold bg-black/60 px-3 py-1 rounded border border-rose-500/20 min-w-[58px] justify-center">
            <i className="fas fa-bomb text-xs"></i>
            <span>{String(minesRemaining).padStart(2, '0')}</span>
          </div>

          {/* Reset / Emotion Button */}
          <button
            onClick={handleResetGame}
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center transition-transform active:scale-95"
            title="Click to reset game"
          >
            <i className={`${getFaceIcon()} text-xl`}></i>
          </button>

          {/* Timer Display */}
          <div className="flex items-center gap-1.5 font-mono text-lg text-blue-400 font-bold bg-black/60 px-3 py-1 rounded border border-blue-500/20 min-w-[58px] justify-center">
            <i className="fas fa-stopwatch text-xs"></i>
            <span>{String(elapsedSeconds).padStart(3, '0')}</span>
          </div>
        </div>

        {/* Status and Mode Bar */}
        <div className="flex items-center justify-between mb-2 sm:mb-2.5 px-1">
          <span className="text-xs text-gray-300 font-medium">
            {gameStatus === 'won' && <span className="text-pink-400 font-semibold">Victory! All clear!</span>}
            {gameStatus === 'lost' && <span className="text-rose-400 font-semibold">Detonated! Try again.</span>}
            {gameStatus === 'playing' && <span>Find {config.mines} hidden mines</span>}
            {gameStatus === 'idle' && <span>Tap any tile to start</span>}
          </span>

          <button
            onClick={() => setFlagMode(!flagMode)}
            className={`px-3 py-1 rounded-lg text-xs font-medium border transition-colors flex items-center gap-1.5 ${
              flagMode
                ? 'bg-rose-500/20 border-rose-400/40 text-rose-300'
                : 'bg-white/10 border-white/10 text-gray-300 hover:bg-white/15'
            }`}
            title="Toggle flag placing mode on tap"
          >
            <i className={flagMode ? 'fas fa-flag text-rose-400' : 'fas fa-crosshairs text-blue-400'}></i>
            <span>{flagMode ? 'Mode: Flag' : 'Mode: Dig'}</span>
          </button>
        </div>

        {/* Minesweeper Grid */}
        <div className="overflow-auto p-1 rounded-xl bg-black/40 border border-white/10">
          <div 
            className="grid gap-[3px] mx-auto w-fit"
            style={{
              gridTemplateColumns: `repeat(${config.cols}, minmax(0, 1fr))`
            }}
            onMouseDown={() => { if (gameStatus === 'playing') setFaceEmotion('scared') }}
            onMouseUp={() => { if (gameStatus === 'playing') setFaceEmotion('normal') }}
          >
            {board.map((row, r) =>
              row.map((cell, c) => {
                const isMineLoss = cell.isRevealed && cell.isMine
                const isSafeRevealed = cell.isRevealed && !cell.isMine

                return (
                  <button
                    key={`${r}-${c}`}
                    onClick={() => handleCellClick(r, c)}
                    onContextMenu={(e) => handleToggleFlag(r, c, e)}
                    disabled={cell.isRevealed && !cell.isMine}
                    className={`aspect-square rounded flex items-center justify-center font-bold transition-colors select-none ${
                      difficulty === 'easy'
                        ? 'w-9 h-9 sm:w-10 sm:h-10 text-sm sm:text-base'
                        : difficulty === 'expert'
                        ? 'w-6 h-6 sm:w-7 sm:h-7 text-[11px] sm:text-xs'
                        : 'w-7 h-7 sm:w-[30px] sm:h-[30px] text-xs sm:text-sm'
                    } ${
                      isMineLoss
                        ? 'bg-rose-600 text-white'
                        : isSafeRevealed
                        ? 'bg-white/5 border border-white/5 cursor-default'
                        : 'bg-white/15 hover:bg-white/25 active:bg-white/30 border border-white/20'
                    }`}
                  >
                    {cell.isFlagged && !cell.isRevealed && (
                      <i className="fas fa-flag text-rose-400 text-xs"></i>
                    )}
                    {cell.isRevealed && cell.isMine && (
                      <i className="fas fa-bomb text-white text-xs"></i>
                    )}
                    {cell.isRevealed && !cell.isMine && cell.neighborMines > 0 && (
                      <span className={getNumberColor(cell.neighborMines)}>
                        {cell.neighborMines}
                      </span>
                    )}
                  </button>
                )
              })
            )}
          </div>
        </div>

        {/* Footer instructions */}
        <div className="mt-2 text-center text-[11px] text-gray-400">
          Desktop: Left-click to dig, Right-click to flag. Mobile: Use mode toggle button.
        </div>
      </div>
    </div>
  )
}
